import { useAuiState, useRemoteThreadListRuntime } from "@assistant-ui/react";
import { type RefObject, useEffect, useRef } from "react";
import { type PrepareMessage, type ToolRunner, useAgentThread } from "@/hooks/use-agent-thread";
import { useRemoteThreadAdapter } from "@/hooks/use-thread-adapter";
import { useWorkspace } from "@/hooks/use-workspace";
import { getSavedModel } from "@/lib/composer-prefs";
import { addEntry } from "@/lib/search-history";
import { threadIdFor } from "@/lib/thread-id";

/**
 * The thread list and the live thread behind it, shared by the new tab and the side panel. Both
 * list the same threads, and a thread open in both is the same ChatAgent Durable Object, so they
 * see the same answer stream (use-agent-thread.ts).
 */
export function useChatRuntime({
  prepareMessage,
  toolRunner,
}: {
  prepareMessage?: RefObject<PrepareMessage | null>;
  /** The side panel's browser tools (ADMIN-217); the new tab has none. */
  toolRunner?: RefObject<ToolRunner | null>;
} = {}) {
  const threadAdapter = useRemoteThreadAdapter();
  const { activeOrganizationId } = useWorkspace();

  const runtime = useRemoteThreadListRuntime({
    runtimeHook() {
      // biome-ignore lint/correctness/useHookAtTopLevel: library hook context
      const id = useAuiState((s: { threadListItem: { id: string } }) => s.threadListItem.id);
      // biome-ignore lint/correctness/useHookAtTopLevel: library hook context
      const remoteId = useAuiState(
        (s: { threadListItem: { remoteId: string | undefined } }) => s.threadListItem.remoteId,
      );
      // biome-ignore lint/correctness/useHookAtTopLevel: library hook context
      const status = useAuiState(
        (s: { threadListItem: { status: string } }) => s.threadListItem.status,
      );
      // Each thread is its own ChatAgent Durable Object, named by the thread id. A new chat gets
      // its UUID now, and keeps it when it's saved (`initialize` reports the same id).
      // biome-ignore lint/correctness/useHookAtTopLevel: library hook context
      const { runtime: perThreadRuntime, chat } = useAgentThread({
        threadId: remoteId ?? threadIdFor(id),
        isNew: status === "new",
        prepareMessage,
        toolRunner,
      });
      // biome-ignore lint/correctness/useHookAtTopLevel: library hook context
      const lastRecorded = useRef<{ msgId: string; hasThreadId: boolean } | null>(null);
      // biome-ignore lint/correctness/useHookAtTopLevel: library hook context
      const userSentRef = useRef(false);

      // biome-ignore lint/correctness/useHookAtTopLevel: library hook context
      useEffect(() => {
        if (chat.status === "submitted") {
          userSentRef.current = true;
        }
      }, [chat.status]);

      // biome-ignore lint/correctness/useHookAtTopLevel: library hook context
      // biome-ignore lint/correctness/useExhaustiveDependencies: activeOrganizationId stable, deps chat-scoped
      useEffect(() => {
        if (!userSentRef.current) return;
        const userMessages = chat.messages.filter((m) => m.role === "user");
        const last = userMessages[userMessages.length - 1];
        if (!last) return;
        const text = last.parts
          ?.filter((p): p is { type: "text"; text: string } => p.type === "text")
          .map((p) => p.text)
          .join("")
          .trim();
        if (text) {
          const hasThreadId = !!remoteId;
          const prev = lastRecorded.current;
          if (prev && prev.msgId === last.id && prev.hasThreadId) return;
          const model = getSavedModel();
          addEntry(
            {
              query: text,
              type: "ai",
              engineOrModel: model,
              threadId: remoteId || undefined,
            },
            activeOrganizationId,
          );
          lastRecorded.current = { msgId: last.id, hasThreadId };
          if (hasThreadId) userSentRef.current = false;
        }
      }, [chat.messages, remoteId]);

      return perThreadRuntime;
    },
    adapter: threadAdapter,
    allowNesting: true,
  });

  return runtime;
}
